import React from 'react';
import { Undo2, Clock } from 'lucide-react';
import { useT } from '../i18n';

/**
 * Le nombre vient du serveur.
 *
 * Il était écrit 5 en dur ici, et la phrase parlait de la *liste* : « History
 * keeps the last 5 operations ». Sur le stockage MongoDB — celui qui tourne en
 * production — la liste n'est pas plafonnée du tout, donc la phrase était
 * fausse là où elle comptait. Ce qui vaut 5 des deux côtés, c'est la
 * profondeur de l'annulation, et c'est ce que la phrase dit maintenant.
 */
const OperationHistory = ({ history, onUndo, canUndo, maxUndo }) => {
  const t = useT();

  /**
   * One line of history.
   *
   * The server sends a code and its parameters so the sentence is written
   * here, in the reader's language. An entry with no code — one recorded
   * before this existed, or a plain string — keeps the sentence it was stored
   * with: English, and true.
   */
  const say = (entry) => {
    if (typeof entry === 'string') return entry;
    return entry?.code ? t(entry.code, entry.params) : entry?.label;
  };
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t('history.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">{t('history.recent')}</h3>
        {canUndo && (
          <button
            onClick={onUndo}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="history-undo-button"
          >
            <Undo2 className="h-4 w-4" />
            {t('history.undoLast')}
          </button>
        )}
      </div>
      <div className="divide-y divide-border">
        {history.map((operation, idx) => (
          <div
            key={idx}
            className="py-3 first:pt-0 last:pb-0"
            data-testid={`history-item-${idx}`}
          >
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium flex-shrink-0 mt-0.5">
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{say(operation)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {maxUndo ? (
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {t('history.limit', { max: maxUndo })}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default OperationHistory;